import React, { useCallback, useEffect, useId, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { Trans, useTranslation } from 'react-i18next';
import { useBreakoutRooms } from 'features/breakout-rooms/useBreakoutRooms';
import {
  selectBreakoutParticipantsLimit,
  selectBreakoutRoomsStatus,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import TargetRoomSelector, {
  SelectedRoomId,
} from 'features/breakout-rooms/components/target-room-selector/TargetRoomSelector';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';
import { admitEntry } from 'features/users/actions';
import { selectUserById } from 'features/users/usersSlice';

const AssignWaitingUserToRoomModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const userId = modalData.userId as string;

  const user = useAppSelector((state) => selectUserById(state, userId));

  const [selectedId, setSelectedId] = useState<SelectedRoomId>();

  const { hasBreakoutManagementAccess } = useBreakoutRooms();

  const participantsLimit = useAppSelector(selectBreakoutParticipantsLimit);

  const breakoutsStatus = useAppSelector(selectBreakoutRoomsStatus);
  const breakoutsStarted = breakoutsStatus === 'started';

  const modalShouldBeClosed = !hasBreakoutManagementAccess || !user || !breakoutsStarted;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('assignWaitingUserToBreakoutRoom'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('assignWaitingUserToBreakoutRoom'));
  };

  const handleOk = () => {
    dispatch(
      admitEntry({
        userId,
        roomId: selectedId,
      })
    );
    dispatch(closeModal('assignWaitingUserToBreakoutRoom'));
  };

  const handleRoomSelect = useCallback((roomId: string) => {
    setSelectedId(roomId);
  }, []);

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
    >
      <DialogTitle id={`${modalId}-title`}>
        {t('breakout_rooms.assign_waiting_user.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id={`${modalId}-description`}>
          <Trans
            i18nKey="modals:breakout_rooms.assign_waiting_user.description"
            t={t}
            values={{
              username: user?.name,
              participantsLimit,
            }}
            components={{
              bold: <strong />,
            }}
          />
        </DialogContentText>
        <Box
          sx={{
            mt: 6,
          }}
        >
          <TargetRoomSelector
            selectedId={selectedId}
            selectRoom={handleRoomSelect}
            participantsLimit={participantsLimit}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('breakout_rooms.assign_waiting_user.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk} disabled={!selectedId}>
          {t('breakout_rooms.assign_waiting_user.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignWaitingUserToRoomModal;
